import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';

const VERSION_URL = '/assets/version.json';
const POLL_INTERVAL_MS = 5 * 60 * 1000;

interface VersionStamp {
	version: string;
	builtAt: string;
}

@Injectable({
	providedIn: 'root'
})
export class VersionCheckService {

	private snackBar = inject(MatSnackBar);
	private translateService = inject(TranslateService);

	private loadedVersion: string | null = null;
	private promptShown: boolean = false;
	private started: boolean = false;

	async start(): Promise<void> {
		if (this.started) {
			return;
		}
		this.started = true;

		this.loadedVersion = await this.fetchVersion();
		if (!this.loadedVersion) {
			return;
		}

		setInterval(() => this.checkForUpdate(), POLL_INTERVAL_MS);
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'visible') {
				this.checkForUpdate();
			}
		});
	}

	private async checkForUpdate(): Promise<void> {
		if (this.promptShown) {
			return;
		}
		const current = await this.fetchVersion();
		if (current && current !== this.loadedVersion) {
			this.promptUpdate();
		}
	}

	private async fetchVersion(): Promise<string | null> {
		try {
			const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' });
			if (!response.ok) {
				return null;
			}
			const stamp = await response.json() as Partial<VersionStamp>;
			return typeof stamp?.version === 'string' && stamp.version ? stamp.version : null;
		} catch {
			return null;
		}
	}

	private promptUpdate(): void {
		this.promptShown = true;
		const snackBarRef = this.snackBar.open(
			this.translate('general.new_version_available', 'A new version is available'),
			this.translate('general.reload', 'Reload'),
			{ horizontalPosition: 'end', verticalPosition: 'bottom', panelClass: 'update-toaster' }
		);
		snackBarRef.onAction().subscribe(() => window.location.reload());
		snackBarRef.afterDismissed().subscribe(() => this.promptShown = false);
	}

	private translate(key: string, fallback: string): string {
		const value = this.translateService.instant(key);
		return !value || value === key ? fallback : value;
	}
}
